// Copyright © 2024 Broadcom Inc. and its subsidiaries. All Rights Reserved.
import React, { Fragment, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { arrayOf, bool, func, number, object, shape } from 'prop-types';
import get from 'lodash/get';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import compose from 'recompose/compose';
import { Grid, withStyles } from '@material-ui/core';
import { lowerCase, startCase } from 'lodash';
import moment from 'moment';
import FiberManualRecordIcon from '@material-ui/icons/FiberManualRecord';
import clsx from 'clsx';
import { HeaderWithTooltip } from '../../../pageComponents/HeaderWithTooltip';
import { fetchApplications, fetchApiPlanDetails, fetchApiGroupDetails } from '../../../actions/application';
import { fetchAutoSuggestions, fetchApi } from '../../../actions/api';
import { getConfig, getUserDetails, getFeatureFlags } from '../../../reducers/portalConfig';
import CertificateIcon from '../icons/certificate.svg';
import KeyIcon from '../icons/key.svg';
import { format } from "date-fns";
import {
  getApplicationsListTotalPages,
  getApplicationsListTotalElements,
  getApplicationsListResults,
  getIsListError,
  getIsLoading,
  getSingleApiGroup,
  getSingleApiPlan,
} from '../../../reducers/application';
import { getAutoSuggestions, getApiDetails } from '../../../reducers/api';
import styles from './styles';
import ListContainer from '../../list';
import {
  GRID_ROWS_PER_PAGE_DEFAULT_OPTION,
  ALERT_ERROR,
  INFO,
  ANY,
  APPLICATION_LIST_STATUS_BY,
  APPLICATION_STATUS_ENABLED,
  APPLICATION_STATUS_PENDING_APPROVAL,
  APPLICATION_STATUS_EDIT_PENDING_APPROVAL,
  APPLICATION_STATUS_REJECTED,
  APPLICATION_STATUS_DELETE_PENDING_APPROVAL,
  APPLICATION_STATUS_DISABLED,
  APPLICATION_STATUS_INCOMPLETE,
  APPLICATION_STATUS_DELETE_FAILED,
  IS_LESS_THAN,
  APPLICATION_LIST_EXPIRY_INTERVAL_FILTER_BY,
  APPLICATION_LIST_EXPIRY_TYPE_FILTER_BY,
  EXPIRY_TYPE_SECRET,
  IS_GREATER_THAN,
  SERVER_DATE_FORMAT,
} from '../../../constants';
import { getI18n, getI18nFormattedMessage } from '../../../utils/intl';
import {
  isOnlyDeveloper,
  hasOrgBoundRole,
} from '../../../utils';
import { FormSelect, FormTextField } from '../../../components';
import { FilterBySelect, FilterByText } from '../../../pageComponents/Filters';

export const getHashParams = (props) => get(props, 'location.hash');

const getExpiryInDays = (earliestExpiryTs, earliestExpiryType, classes,
   startReportingDays , urgentReportingDays ) => {
  if (!earliestExpiryTs) {
    return (<span />)
  }
  const expiryTime = moment(earliestExpiryTs);
  const currentTime = moment();
  const days = expiryTime.diff(currentTime, 'days');
  let label = '';
  let className = classes.normalKey;
  let expiryStatus;
    label = days;
    if (days <= urgentReportingDays) {
      className = classes.urgentReportingKey;
      expiryStatus = 'urgentReporting';
      if (days < 0) {
        label = 0;
      }
    } else if (days > startReportingDays) {
      className = classes.normalKey;
      expiryStatus = 'normal';
    } else {
      className = classes.startReportingKey;
      expiryStatus = 'startReporting';
    }
  return (
    <span
    data-apim-test={`app-cert-expiry-status-${expiryStatus}`}
    className={clsx(classes.status, className)}
  >
    {label} <img src={ earliestExpiryType === 'SECRET' ? KeyIcon : CertificateIcon}
            className={classes.certOrKeyIcon}
          />
  </span>);
};

const getAppListColumns = (isOrgUser, classes, startReportingDays, urgentReportingDays) => {
  const columnList = [];
  columnList.push({
    id: 'name',
    label: getI18nFormattedMessage('label.applicaiton.list.page.application.name.column'),
    minWidth: 200,
    link: '/admin/console/applications/details',
  });
  if(!isOrgUser) {
    columnList.push({
      id: 'organizationName',
      label: getI18nFormattedMessage('label.applicaiton.list.page.organization.name.column'),
      minWidth: 200,
    });
  }
  columnList.push({
    id: 'daystocertificateexpiry',
    label: (
      <HeaderWithTooltip
            classes={classes}
            label={getI18nFormattedMessage('label.application.list.page.daystocertificateexpiry.column')}
            tooltipTitle={getI18nFormattedMessage('label.application.list.page.daystocertificateexpiry.tooltip')}
          />
    ),
    minWidth: 200,
    value: (item) => getExpiryInDays(item.earliestExpiryTs, item.earliestExpiryType, classes,
      startReportingDays, urgentReportingDays ),
  });
  columnList.push({
    id: 'status',
    label: getI18nFormattedMessage('label.applicaiton.list.page.status.column'),
    minWidth: 200,
    value: (item) => formatAppStatus(item.status, classes),
  });
  return columnList;
}

export const getStatusColorClass = (status, classes) => {
  switch (status) {
    case APPLICATION_STATUS_ENABLED:
      return classes.available;
    case APPLICATION_STATUS_PENDING_APPROVAL:
    case APPLICATION_STATUS_EDIT_PENDING_APPROVAL:
    case APPLICATION_STATUS_DELETE_PENDING_APPROVAL:
      return classes.pending;
    case APPLICATION_STATUS_REJECTED:
    case APPLICATION_STATUS_INCOMPLETE:
    case APPLICATION_STATUS_DELETE_FAILED:
      return classes.rejected;
    case APPLICATION_STATUS_DISABLED:
      return classes.disabled;
  }
};
const formatAppStatus = (status, classes) => {
  return (
    <span className={classes.statusField}>
      <FiberManualRecordIcon
        className={clsx(classes.statusIcon, getStatusColorClass(status, classes))}/>
      <span>{startCase(lowerCase(status))}</span>
    </span>);
}
export const ApplicationList = (props) => {
  const {
    classes,
    userContext,
    isError,
    isLoading,
    totalPages,
    totalElements = 0,
    results = [],
    fetchApi,
    fetchApiGroupDetails,
    fetchApiPlanDetails,
    apiPlanDetails,
    apiGroupDetails,
    featureFlags,
  } = props;

  const intl = getI18n(useIntl());
  const [notificationMessage, setNotificationMessage] = useState('');
  const [notificationStatus, setNotificationStatus] = useState('');
  const [filterByName, setFilterByName] = useState('');
  const [filterByOrgName, setFilterByOrgName] = useState('');
  const [filterByStatus, setFilterByStatus] = useState(ANY);
  const [filterByExpiryInterval, setFilterByExpiryInterval] = useState(IS_LESS_THAN);
  const [filterByExpiryType, setFilterByExpiryType] = useState(EXPIRY_TYPE_SECRET);
  const [filterByExpiryDays, setFilterByExpiryDays] = useState('');
  const [appliedExpiryDaysFilter, setAppliedExpiryDaysFilter] = useState('');
  const [filterByXref, setFilterByXref] = useState('');
  const [appliedNameFilter, setAppliedNameFilter] = useState('');
  const [appliedOrgFilter, setAppliedOrgFilter] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(GRID_ROWS_PER_PAGE_DEFAULT_OPTION);
  const [hideAddButton, setHideAddButton] = useState(true);
  const [filterByXrefUuid, setFilterByXrefUuid] = useState('');
  const [statusFilterList, setStatusFilterList] = useState(APPLICATION_LIST_STATUS_BY);
  const [expiryIntervalFilterList] = useState(APPLICATION_LIST_EXPIRY_INTERVAL_FILTER_BY);
  const [expiryTypeFilterList] = useState(APPLICATION_LIST_EXPIRY_TYPE_FILTER_BY);
  const [filterEarliestExpiryStart, setFilterEarliestExpiryStart] = useState();
  const [filterEarliestExpiryEnd, setFilterEarliestExpiryEnd] = useState();
  const notifyMessages = (status, message) => {
    setNotificationStatus(status);
    setNotificationMessage(message);
  };
  const apiKeySettings = JSON.parse(get(featureFlags, 'apiKeyExpiry.value'));
  const startReportingDays = get(apiKeySettings, 'startReportingBeforeDays', 90);
  const urgentReportingDays = get(apiKeySettings, 'reportAsUrgentBeforeDays', 60);
  const hashParams = getHashParams(props);
  const fetchApplicationsOnLoad = () => {
    const payload = {
      page,
      size: rowsPerPage,
    };
    if (hashParams) {
      const hashArray = hashParams.split("/");
      if (hashArray && hashArray.length === 2) {
        const entityType = hashArray[0].substring(1);
        const uuid = hashArray[1];
        if(entityType === "api" || entityType === "apiPlan" || entityType === "apiGroup") {
          if(entityType === "api") {
            fetchApi(uuid);
          } else {
            if (entityType === "apiPlan") {
              fetchApiPlanDetails(uuid);
            } else if (entityType === "apiGroup") {
              fetchApiGroupDetails(uuid);
            }
            setFilterByXref(entityType);
            setFilterByXrefUuid(uuid);
          }
          payload[`${entityType}Uuid`] = uuid;
        }
      }
    }
    props.fetchApplications(payload, hasOrgBoundRole(userContext));
  };

  const onCloseNotification = () => {
    window.location.href = '/admin/console/applications';
  };

  const isFilterDirty = () =>
    filterByName !== '' ||
    filterByOrgName !== ''||
    filterByStatus !== ANY ||
    filterByXref !== '' ||
    filterByExpiryInterval !== IS_LESS_THAN ||
    filterByExpiryType !== EXPIRY_TYPE_SECRET;

  const retrieveApplications = (filtersAndPages) => {
    const payload = {
      name: filtersAndPages.name || filterByName,
      orgName: filtersAndPages.orgName || filterByOrgName,
      status: filtersAndPages.status || filterByStatus,
      page: filtersAndPages.page || 0,
      size: filtersAndPages.size || rowsPerPage,
      earliestExpiryStart: !filtersAndPages.ignoreDateFilters ? filtersAndPages.earliestExpiryStart || filterEarliestExpiryStart : '',
      earliestExpiryEnd: !filtersAndPages.ignoreDateFilters ? filtersAndPages.earliestExpiryEnd || filterEarliestExpiryEnd: '',
      earliestExpiryType: !filtersAndPages.ignoreDateFilters ?
      filtersAndPages.filterByExpiryType : '',
    };
    if(filterByXrefUuid) {
      payload[`${filterByXref}Uuid`] = filterByXrefUuid;
    }
    props.fetchApplications(payload, hasOrgBoundRole(userContext));
  };
  useEffect(() => {
    if(apiPlanDetails && apiPlanDetails.name) {
      notifyMessages(INFO,
        `Currently filtering Applications by API Plan → ${apiPlanDetails.name}`);
    }
  }, [apiPlanDetails]);
  useEffect(() => {
    if(apiGroupDetails && apiGroupDetails.name) {
      notifyMessages(INFO,
        `Currently filtering Applications by API Group → ${apiGroupDetails.name}`);
    }
  }, [apiGroupDetails]);

  useEffect(() => {
    fetchApplicationsOnLoad();
  }, []);
  const isOrgUser = hasOrgBoundRole(userContext);
  useEffect(() => {
    const isDev = isOnlyDeveloper(userContext);
    setHideAddButton(isDev);
    if (!isOrgUser) {
      setStatusFilterList(APPLICATION_LIST_STATUS_BY.filter(status =>
        status.uuid !== APPLICATION_STATUS_REJECTED));
    } else if (isDev) {
      setStatusFilterList(APPLICATION_LIST_STATUS_BY.filter(status =>
        status.uuid !== APPLICATION_STATUS_INCOMPLETE));
    }
  }, [userContext]);

  useEffect(() => {
    if (isError) {
      notifyMessages(ALERT_ERROR, intl.getI18nMessage('error.applications.list.fetch'));
    }
  }, [isError]);

  const onFilterByNameChange = (value) => {
    setFilterByName(value);
  };

  const onFilterByOrgNameChange = (value) => {
    setFilterByOrgName(value);
  };

  const onFilterByExpiryDaysChange = (value) => {
    setFilterByExpiryDays(value);
  };

  const onFilterByStatusChange = (newStatus) => {
    setFilterByStatus(newStatus);
    retrieveApplications({ status: newStatus });
  };

  const getFilterByExpiryStartAndEnd = (filterByExpiryDays, filterByExpiryInterval) =>
  {
    const currentTime = moment().toDate();
    let startTime, endTime, startTimeEncodeURI, endTimeEncodeURI;
    if(filterByExpiryInterval === IS_LESS_THAN) {
      startTime = currentTime;
      endTime = moment().add(filterByExpiryDays, 'days').toDate();
      startTimeEncodeURI = format(startTime, SERVER_DATE_FORMAT);
      endTimeEncodeURI = format(endTime, SERVER_DATE_FORMAT);
    } else if (filterByExpiryInterval === IS_GREATER_THAN) {
      startTime = moment().add(filterByExpiryDays, 'days').toDate();
      startTimeEncodeURI = format(startTime, SERVER_DATE_FORMAT);
    }
    return {
      startTime: startTimeEncodeURI,
      endTime: endTimeEncodeURI,
    };
  }

  const onFilterByExpiryIntervalChange = (newInterval) => {
    setFilterByExpiryInterval(newInterval);
    if (filterByExpiryDays) {
      const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByExpiryDays, newInterval);
      setFilterEarliestExpiryStart(startTime);
      setFilterEarliestExpiryEnd(endTime);
      retrieveApplications({ earliestExpiryStart : startTime, earliestExpiryEnd: endTime });
    }
  };

  const onFilterByExpiryTypeChange = (newExpiryType) => {
    setFilterByExpiryType(newExpiryType);
    if (filterByExpiryDays) {
      const { startTime, endTime } =
        getFilterByExpiryStartAndEnd(filterByExpiryDays, filterByExpiryInterval);
      setFilterEarliestExpiryStart(startTime);
      setFilterEarliestExpiryEnd(endTime);
      retrieveApplications({ earliestExpiryStart: startTime, earliestExpiryEnd: endTime,
        filterByExpiryType: newExpiryType });
    }
  };

  const onFilterByNameKeyPress = (e) => {
    if (e.key === 'Enter' && filterByName !== appliedNameFilter) {
      retrieveApplications({ });
      setAppliedNameFilter(filterByName);
    }
  };
  const onFilterByOrgKeyPress = (e) => {
    if (e.key === 'Enter' && filterByOrgName !== appliedOrgFilter) {
      retrieveApplications({ });
      setAppliedOrgFilter(filterByOrgName);
    }
  };

  const onFilterByDaysKeyPress = (e) => {
    if (e.key === 'Enter' && filterByExpiryDays !== appliedExpiryDaysFilter) {
      setAppliedExpiryDaysFilter(filterByExpiryDays);
      const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByExpiryDays,
         filterByExpiryInterval);
      if(filterByExpiryDays) {
        setFilterEarliestExpiryStart(startTime);
        setFilterEarliestExpiryEnd(endTime);
        retrieveApplications({ earliestExpiryStart : startTime, earliestExpiryEnd: endTime });
      } else {
        setFilterEarliestExpiryStart('');
        setFilterEarliestExpiryEnd('');
        retrieveApplications({ ignoreDateFilters : true });
      }
    }
  };

  const handleBlurName = () => {
    if(filterByName !== appliedNameFilter) {
      retrieveApplications({ });
      setAppliedNameFilter(filterByName);
    }
  };

  const handleBlurOrgName = () => {
    if(filterByOrgName !== appliedOrgFilter) {
      retrieveApplications({ });
      setAppliedOrgFilter(filterByOrgName);
    }
  };

  const handleBlurExpiryDays = () => {
    if(filterByExpiryDays !== appliedExpiryDaysFilter) {
      setAppliedExpiryDaysFilter(filterByExpiryDays);
      if (filterByExpiryDays) {
        const { startTime, endTime } = getFilterByExpiryStartAndEnd(filterByExpiryDays,
          filterByExpiryInterval);
       setFilterEarliestExpiryStart(startTime);
       setFilterEarliestExpiryEnd(endTime);
       retrieveApplications(
        { earliestExpiryStart : startTime, earliestExpiryEnd: endTime, filterByExpiryType });
      } else {
       setFilterEarliestExpiryStart('');
       setFilterEarliestExpiryEnd('');
       retrieveApplications({ ignoreDateFilters : true });
      }
    }
  };

  const onChangePage = (newPage) => {
    if (page === newPage) { return; }
    setPage(newPage);
    retrieveApplications({ page: newPage });
  };
  const onChangePreviousPage = () => { onChangePage(page - 1); };
  const onChangeNextPage = () => { onChangePage(page + 1); };

  const onChangeRowsPerPage = (newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    retrieveApplications({ size: newRowsPerPage });
  };

  const onAddApplication = () => {
    props.push('/admin/console/applications/add');
  };
  const noResultsMessage = isFilterDirty() ?
    intl.getI18nMessage('label.application.filter.no.results') :
    intl.getI18nMessage('label.application.no.results');

  return (
    <ListContainer
      listPageId="application-list-page"
      isLoading={isLoading}
      notificationId="application-notifications"
      notificationStatus={notificationStatus}
      setNotificationStatus={setNotificationStatus}
      notificationMessage={notificationMessage}
      setNotificationMessage={setNotificationMessage}
      onCloseNotification={onCloseNotification}
      closeNotificationText={intl.getI18nMessage('label.application.list.notification.close.button.text')}
      pageHeaderTitle={intl.getI18nMessage('label.application.list.page.title')}
      addButtonLabel={intl.getI18nMessage('label.application.list.add.button')}
      onAdd={!hideAddButton && onAddApplication}
      pageFilterAndSortClass={classes.pageFilterAndSort}
      filterAndSortContent={!filterByXref || filterByXref==='api' ? (
        <div className={classes.textFiltersContainer}>
        <Fragment>
          <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
            <FormTextField
              name={intl.getI18nMessage('label.filter')}
              value={filterByName}
              placeholder={intl.getI18nMessage('label.application.list.page.filter.by.name.placeholder')}
              handleChange={onFilterByNameChange}
              onKeyPress={onFilterByNameKeyPress}
              handleBlur={handleBlurName}
              id="applications-filter-by-name"
              data-apim-test="applications-filter-by-name"
            />
          </Grid>
          {!isOrgUser &&
            (<Grid item md={3} sm={3} xs={12} className={classes.filterField}>
              <FormTextField
                {...props}
                id="applications-filter-by-org-name"
                data-apim-test="applications-filter-by-org-name"
                name="Filter"
                value={filterByOrgName}
                textFieldClass={classes.textField}
                placeholder={intl.getI18nMessage('label.application.list.page.filter.by.orgname.placeholder')}
                handleChange={onFilterByOrgNameChange}
                onKeyPress={onFilterByOrgKeyPress}
                handleBlur={handleBlurOrgName}
                hideLabel
              />
            </Grid>
          )}
          <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
          <FilterBySelect
              {...props}
              id="applications-filter-by-expiryType"
              data-apim-test="applications-filter-by-expiryType"
              name={intl.getI18nMessage('label.filter')}
              value={filterByExpiryType}
              data={expiryTypeFilterList}
              handleChange={onFilterByExpiryTypeChange}
              hideLabel
              noNoneOption
              noNativeSelect
            />
            </Grid>
          <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
            <FilterBySelect
              {...props}
              id="applications-filter-by-expiryInterval"
              data-apim-test="applications-filter-by-expiryInterval"
              name={intl.getI18nMessage('label.filter')}
              value={filterByExpiryInterval}
              data={expiryIntervalFilterList}
              handleChange={onFilterByExpiryIntervalChange}
              hideLabel
              noNoneOption
              noNativeSelect
            />
            </Grid>
          <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
            <FilterByText
              {...props}
              id="days"
              data-apim-test="days"
              name="Filter"
              type="number"
              value={filterByExpiryDays}
              textFieldClass={classes.textField}
              placeholder={'Days'}
              handleChange={onFilterByExpiryDaysChange}
              onKeyPress={onFilterByDaysKeyPress}
              handleBlur={handleBlurExpiryDays}
              hideLabel
            />
          </Grid>
          <Grid item md={3} sm={3} xs={12} className={classes.filterField}>
            <FormSelect
              {...props}
              id="applications-filter-by-status"
              data-apim-test="applications-filter-by-status"
              name={intl.getI18nMessage('label.filter')}
              value={filterByStatus}
              data={statusFilterList}
              handleChange={onFilterByStatusChange}
              hideLabel
              noNoneOption
              noNativeSelect
            />
          </Grid>
        </Fragment>
        </div>
      ): null }
      columns={getAppListColumns(isOrgUser, classes, startReportingDays, urgentReportingDays)}
      rows={results}
      noResultsMessage={noResultsMessage}
      page={page}
      totalElements={totalElements}
      totalPages={totalPages}
      rowsPerPage={rowsPerPage}
      onChangeRowsPerPage={onChangeRowsPerPage}
      onChangePage={onChangePage}
      onChangePreviousPage={onChangePreviousPage}
      onChangeNextPage={onChangeNextPage}
    />
  );
};

ApplicationList.propTypes = {
  classes: shape({ }),
  userContext: shape({ }),
  isLoading: bool,
  isError: bool,
  push: func,
  totalPages: number,
  totalElements: number,
  results: arrayOf(shape({ })),
  autoSuggestions: arrayOf(shape({ })),
  fetchApplications: func,
  fetchAutoSuggestions: func,
  fetchApi: func,
  fetchApiGroupDetails: func,
  fetchApiPlanDetails: func,
  apiDetails: shape({ }),
  apiGroupDetails: shape({ }),
  apiPlanDetails: shape({ }),
  featureFlags:object,
};

const mapStateToProps = (state) => ({
  config: getConfig(state),
  userContext: getUserDetails(state),
  isError: getIsListError(state),
  isLoading: getIsLoading(state),
  totalPages: getApplicationsListTotalPages(state),
  totalElements: getApplicationsListTotalElements(state),
  results: getApplicationsListResults(state),
  autoSuggestions: getAutoSuggestions(state),
  apiDetails: getApiDetails(state),
  apiPlanDetails: getSingleApiPlan(state),
  apiGroupDetails: getSingleApiGroup(state),
  featureFlags: getFeatureFlags(state),
});

const mapDispatchToProps = {
  push,
  fetchApplications,
  fetchAutoSuggestions,
  fetchApi,
  fetchApiGroupDetails,
  fetchApiPlanDetails,
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, mapDispatchToProps),
)(ApplicationList);
